import { Link } from 'react-router-dom'
import {ArrowUpRight, ArrowRight} from 'lucide-react'
import PageCTA from '@/components/PageCTA'
import SEO from '@/components/SEO'
import { home, images, links } from '@/content/site'

function Home() {
  const offerImages = {
    handsBrush: images.handsBrush,
    canevasOffer: images.canevasOffer,
    atelierRoom: images.atelierRoom,
    paintDetail: images.paintDetail,
    gesteLibre: images.gesteLibre,
    deuil: images.deuil,
  }

  return (
    <div>
      <SEO
        title="HeartCanvas® — L'art comme chemin de reconstruction | Suisse"
        description="Alexandra accompagne adultes et jeunes à travers la transformation par l'art. Parcours individuel, ateliers institutionnels en Suisse."
        path="/"
      />
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 md:px-10 overflow-hidden">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-end">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-8 animate-fade-in">
              {home.hero.eyebrow || '— HeartCanvas'}
            </div>
            <h1 className="font-serif-display text-[36px] md:text-[60px] leading-[1.05] text-[#1F1B1D]">
              <span className="block animate-fade-up">{home.hero.titleLines[0]}</span>
              <span className="block italic text-[#9E3A5A] animate-fade-up delay-200 mt-2">{home.hero.titleLines[1]}</span>
            </h1>
            <p className="mt-8 font-serif-display italic text-[18px] md:text-[20px] leading-[1.55] text-[#3A3236] max-w-lg animate-fade-up delay-600">
              {home.hero.subtitle}
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-up delay-700">
              <Link
                to={home.hero.primaryCta.to}
                className="group inline-flex items-center justify-between gap-4 px-7 py-4 bg-[#9E3A5A] text-[#F6EFF4] text-[13px] tracking-[0.2em] uppercase hover:bg-[#7A2D46] transition-colors duration-500"
              >
                {home.hero.primaryCta.label}
                <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to={home.hero.secondaryCta.to}
                className="group inline-flex items-center justify-between gap-4 px-7 py-4 border border-[#9E3A5A] text-[#9E3A5A] text-[13px] tracking-[0.2em] uppercase hover:bg-[#9E3A5A] hover:text-[#F6EFF4] transition-colors duration-500"
              >
                {home.hero.secondaryCta.label}
                <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </div>

            <p className="mt-6 text-[13px] md:text-[14px] leading-[1.6] text-[#3A3236]/80 italic max-w-lg animate-fade-up delay-700">
              {home.hero.instLine}
            </p>
          </div>

          <div className="md:col-span-5 relative animate-fade-in delay-300">
            <div className="absolute -top-6 -left-6 w-32 h-32 border border-[#9E3A5A]/30 hidden md:block" />
            <img
              src={images.studioTable}
              alt="Atelier HeartCanvas — table de travail avec peinture, pinceaux et palette de couleurs"
              loading="eager"
              className="relative w-full aspect-[3/4] object-cover grayscale-[0.05]"
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-24 md:mt-32 h-px bg-[#DCC9D2] origin-left animate-draw-line delay-700" />
      </section>

      {/* RECONNAISSANCE */}
      <section className="px-6 md:px-10 py-24 md:py-36">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5 md:sticky md:top-32 md:self-start">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{home.reconnaissance.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] whitespace-pre-line">
              {home.reconnaissance.title}
            </h2>
            <img
              src="https://static.lumi.new/material/eb/eb940d13a1a21b451a4993ee2212021f.png"
              alt="Détail d'une œuvre — matière et texture de peinture HeartCanvas"
              loading="lazy"
              className="mt-12 w-full max-w-sm aspect-[3/4] object-cover"
            />
          </div>

          <div className="md:col-span-7 md:pt-4">
            <ul className="divide-y divide-[#DCC9D2]">
              {home.reconnaissance.items.map((line, i) => (
                <li key={i} className="py-6 md:py-7 flex gap-6 items-baseline">
                  <span className="font-serif-display italic text-[#9E3A5A] text-lg w-8 shrink-0">0{i + 1}</span>
                  <p className="font-serif-display text-2xl md:text-[28px] leading-[1.35] text-[#1F1B1D]">{line}</p>
                </li>
              ))}
            </ul>

            <div className="mt-16 pl-8 border-l-2 border-[#9E3A5A]">
              <p className="font-serif-display italic text-2xl md:text-3xl text-[#3A3236] leading-[1.4]">
                {home.reconnaissance.quoteSoft}<br />
                <span className="text-[#1F1B1D]">{home.reconnaissance.quoteStrong}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MÉTHODE */}
      <section className="bg-[#EFE5EC] border-y border-[#DCC9D2] px-6 md:px-10 py-24 md:py-36">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-24">
            <div className="md:col-span-4" />
            <div className="md:col-span-8">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{home.methode.eyebrow}</div>
              <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D]">
                {home.methode.title}
              </h2>
              <p className="mt-6 font-serif-display italic text-[18px] md:text-[20px] leading-[1.55] text-[#3A3236] max-w-2xl">
                {home.methode.intro}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#DCC9D2]">
            {home.methode.steps.map(step => (
              <div key={step.num} className="bg-[#EFE5EC] p-10 md:p-12 hover:bg-[#F6EFF4] transition-colors duration-500">
                <div className="font-serif-display italic text-5xl text-[#9E3A5A] mb-8">{step.num}</div>
                <h3 className="font-serif-display text-[26px] md:text-[32px] text-[#1F1B1D] mb-4 leading-tight">{step.title}</h3>
                <p className="text-[16px] leading-[1.75] text-[#3A3236]">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MÉTHODE EN VIDÉO */}
      <section className="px-6 md:px-10 py-24 md:py-36">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-5">{home.videoMethode.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] mb-4">
              {home.videoMethode.title}
            </h2>
            <p className="font-serif-display italic text-[18px] md:text-[20px] leading-[1.55] text-[#3A3236]">
              {home.videoMethode.subtitle}
            </p>
          </div>
          <div className="flex justify-center">
            <div className="w-full" style={{ maxWidth: '400px' }}>
              <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${home.videoMethode.youtubeId}?autoplay=0&mute=0&controls=1&playsinline=1`}
                  title={home.videoMethode.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFFRES */}
      <section className="px-6 md:px-10 py-24 md:py-36">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20">
            <div className="md:col-span-4" />
            <div className="md:col-span-8">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{home.offres.eyebrow}</div>
              <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] text-[#1F1B1D] whitespace-pre-line">
                {home.offres.title}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            {home.offres.items.map((offer, i) => (
              <Link key={i} to={offer.to} className="group block">
                <div className="relative overflow-hidden mb-6 aspect-[4/5] bg-[#EFE5EC]">
                  <img
                    src={offerImages[offer.image]}
                    alt={`${offer.title} — proposition HeartCanvas`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                  />
                  {offer.meta && (
                    <div className="absolute top-4 left-4 bg-[#F6EFF4] px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase text-[#9E3A5A] border border-[#DCC9D2]">
                      {offer.meta}
                    </div>
                  )}
                </div>
                <h3 className="font-serif-display text-[26px] md:text-[32px] text-[#1F1B1D] leading-tight mb-4">
                  {offer.title}
                </h3>
                <p className="text-[16px] leading-[1.7] text-[#3A3236] mb-6">{offer.body}</p>
                <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.25em] uppercase text-[#1F1B1D] link-underline">
                  {offer.cta} <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* QUI JE SUIS — À PROPOS COURT */}
      <section className="bg-[#F6EFF4] border-y border-[#DCC9D2] px-6 md:px-10 py-28 md:py-40">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <div className="relative">
              <img
                src={images.alexandra}
                alt="Alexandra Goris, fondatrice de HeartCanvas et accompagnante en transformation par l'art"
                loading="lazy"
                className="w-full max-w-md aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 hidden md:block w-32 h-32 border border-[#9E3A5A]/30" />
            </div>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C0923F] mb-6">{home.quiTuEs.eyebrow}</div>
            <h2 className="font-serif-display text-[28px] md:text-[44px] leading-[1.1] mb-8 text-[#2C2C2C]">
              {home.quiTuEs.title}
            </h2>
            <div className="space-y-6 font-serif-display text-[18px] md:text-[20px] leading-[1.6] text-[#2C2C2C] max-w-2xl">
              {home.quiTuEs.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <Link
              to={home.quiTuEs.cta.to}
              className="mt-12 inline-flex items-center gap-3 text-[12px] tracking-[0.25em] uppercase text-[#9E3A5A] link-underline"
            >
              {home.quiTuEs.cta.label} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* INVITATION FINALE */}
      <PageCTA
        eyebrow={home.invitationFinale.eyebrow}
        title={home.invitationFinale.title}
        body={home.invitationFinale.body}
        ctaLabel={home.invitationFinale.ctaLabel}
        ctaHref={links.booking}
      />
    </div>
  )
}

export default Home
